import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getCookiePolicyAction,
  getGuideLineAction,
  getRefundPolicyAction,
  getTermsAction,
  getPrivacyPolicyAction,
} from "../../../../redux/Auth/AuthSlice";
import { useTranslation } from "react-i18next";

function BodyCookiePolicy() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState("privacyPolicy");

  useEffect(() => {
    const pathToPage = {
      "/privacyPolicy": "privacyPolicy",
      "/termsConditions": "termsConditions",
      "/refundPolicy": "refundPolicy",
      "/cookiePolicy": "cookiePolicy",
      "/about": "about",
      "/dataDeletionPolicy": "dataDeletionPolicy",
    };
    setCurrentPage(pathToPage[location.pathname] || "privacyPolicy");
  }, [location.pathname]);

  const handleNavigation = (page, path) => {
    setCurrentPage(page);
    navigate(path);
  };

  const renderContent = () => {
    switch (currentPage) {
      case "privacyPolicy":
        return <PrivacyPolicy />;
      case "termsConditions":
        return <TermsConditions />;
      case "refundPolicy":
        return <RefundPolicy />;
      case "cookiePolicy":
        return <CookiePolicy />;
      case "dataDeletionPolicy":
        return <DataDeletionPolicy />;
      case "about":
        return <About />;
      default:
        return <PrivacyPolicy />;
    }
  };

  return (
    <div className="cookie-policy-container">
      {/* Sidebar */}
      <div className="policy-sidebar">
        <ul>
          {[
            {
              id: "privacyPolicy",
              label: "Privacy Policy",
              path: "/privacyPolicy",
            },
            {
              id: "termsConditions",
              label: "Terms & Conditions",
              path: "/termsConditions",
            },
            {
              id: "refundPolicy",
              label: "Refund Policy",
              path: "/refundPolicy",
            },
            {
              id: "cookiePolicy",
              label: "Cookie Policy",
              path: "/cookiePolicy",
            },
            {
              id: "dataDeletionPolicy",
              label: "Data Deletion Policy",
              path: "/dataDeletionPolicy",
            },
            { id: "about", label: "About", path: "/about" },
          ].map(({ id, label, path }) => (
            <li key={id}>
              <button
                className={currentPage === id ? "active" : ""}
                onClick={() => handleNavigation(id, path)}
              >
                {t(label)}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Main Content */}
      <div className="policy-content">{renderContent()}</div>
    </div>
  );
}

function About() {
  const { t } = useTranslation();

  return (
    <div
      className="about-app content-container"
    >
      <div className="text-center">
        <h2
          className="title-underline"
        >
          {t("About Header")}
        </h2>
      </div>

      <p className="mb-1rem text-left indent-2em">
        {t("About Web")}
      </p>
    </div>
  );
}

function PrivacyPolicy() {
  const dispatch = useDispatch();
  const todos = useSelector((state) => state);
  const { loading, error } = useSelector((state) => state.auth || {});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(getPrivacyPolicyAction());
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching cookie policy:", error);
        setIsLoading(false);
      }
    };
    fetchData();
  }, [dispatch]);

  const categories = todos.AuthSlice.privacyPolicy?.results || [];

  return (
    <div>
      {categories.map((category, index) => (
        <div key={index}>
          <p dangerouslySetInnerHTML={{ __html: category.description }} />
        </div>
      ))}
    </div>
  );
}

function TermsConditions() {
  const dispatch = useDispatch();
  const todos = useSelector((state) => state);
  const { loading, error } = useSelector((state) => state.auth || {});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(getTermsAction());
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching cookie policy:", error);
        setIsLoading(false);
      }
    };
    fetchData();
  }, [dispatch]);

  if (isLoading || loading) return <p>Loading Terms and Conditions...</p>;
  if (error) return <p>Error loading Terms and Conditions: {error}</p>;

  const categories = todos.AuthSlice.terms?.results || [];

  return (
    <div>
      {categories.map((category, index) => (
        <div key={index}>
          <p dangerouslySetInnerHTML={{ __html: category.description }} />
        </div>
      ))}
    </div>
  );
}

function RefundPolicy() {
  const dispatch = useDispatch();
  const todos = useSelector((state) => state);
  const { loading, error } = useSelector((state) => state.auth || {});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(getRefundPolicyAction());
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching refund policy:", error);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [dispatch]);

  if (isLoading || loading) return <p>Loading refund policy...</p>;
  if (error) return <p>Error loading refund policy: {error}</p>;

  const categories = todos.AuthSlice.refundPolicy?.results || [];

  return (
    <div>
      {categories.map((category, index) => (
        <div key={index}>
          <p dangerouslySetInnerHTML={{ __html: category.description }} />
        </div>
      ))}
    </div>
  );
}

function CookiePolicy() {
  const dispatch = useDispatch();
  const todos = useSelector((state) => state);
  const { loading, error } = useSelector((state) => state.auth || {});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(getCookiePolicyAction());
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching cookie policy:", error);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [dispatch]);

  if (isLoading || loading) return <p>Loading cookie policy...</p>;
  if (error) return <p>Error loading cookie policy: {error}</p>;

  const categories = todos.AuthSlice.cookiePolicy?.results || [];

  return (
    <div>
      {categories.map((category, index) => (
        <div key={index}>
          <p dangerouslySetInnerHTML={{ __html: category.description }} />
        </div>
      ))}
    </div>
  );
}

function DataDeletionPolicy() {
  const dispatch = useDispatch();
  const todos = useSelector((state) => state);
  const { loading, error } = useSelector((state) => state.auth || {});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(getGuideLineAction());
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching data deletion policy:", error);
        setIsLoading(false);
      }
    };
    fetchData();
  }, [dispatch]);

  if (isLoading || loading) return <p>Loading data deletion policy...</p>;
  if (error) return <p>Error loading data deletion policy: {error}</p>;

  const categories = todos.AuthSlice.guideLine?.results || [];

  return (
    <div>
      {categories.map((category, index) => (
        <div key={index}>
          <p dangerouslySetInnerHTML={{ __html: category.description }} />
        </div>
      ))}
    </div>
  );
}

export default BodyCookiePolicy;
